import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useProfileStore } from "@/store/profile-store";
import { AuthService } from "@/services/auth-service";
import { isEitaaMiniApp } from "@/utils/utilities";

/**
 * Ends the session: best-effort server-side revocation, then a local clear
 * regardless of the result. Shared by the profile page and the desktop
 * sidebar so session teardown has exactly one implementation.
 */
export function useLogout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tokens, setTokens, setProfile } = useProfileStore();
  const [loggingOut, setLoggingOut] = useState(false);

  // Logout is only meaningful on the web app — the Eitaa miniapp
  // re-authenticates on every launch, so there's nothing to sign out of there.
  const canLogout = !isEitaaMiniApp();

  const logout = useCallback(async () => {
    setLoggingOut(true);
    try {
      const refreshToken = tokens?.refresh_token;
      if (refreshToken) {
        await AuthService.logout(refreshToken).catch(() => {});
      }
    } finally {
      setTokens(null);
      setProfile(null);
      // Drop cached/persisted data so the next session starts clean.
      queryClient.clear();
      setLoggingOut(false);
      toast.success("از حساب خود خارج شدید.");
      navigate("/", { replace: true });
    }
  }, [tokens, setTokens, setProfile, queryClient, navigate]);

  return { logout, loggingOut, canLogout };
}
